/**
 * What a board item is drawn as and how it looks, pure: shared by the board (`windows/graph/board`)
 * and the GUI packs (`packs/<id>`: UI/UX's prototype draws with it), so an item looks the same
 * on the board, in a pack's window and in what it exports.
 *
 * An item is drawn as its kind says (`NodeKindDef.board`, followed along the kind's `like`s:
 * {@link boardLook}): an element, its usual size, words and look when drawn, and the tool that
 * draws it; a kind saying nothing, or naming an element nobody draws, is a card. The elements are
 * data ({@link ElementDef}): the board's own ({@link BOARD_ELEMENTS}: note, text, shape, image,
 * drawing, card) and those a GUI pack adds (UI/UX's button, input...), given to the `kinds`
 * service once. An item's look is its `style`, values by key: `fill`, `stroke`, `color`, `size`,
 * `weight`, `italic`, `underline`, `strike`, `align`, `font`, `radius`, `fit` (a picture: `cover`
 * or `contain`), and whatever a pack's element reads. Values are checked before they become CSS:
 * a colour is a colour, a size a number, so nothing typed can reach another property or fetch
 * anything.
 */
import type { Component } from "solid-js";
import type { BoardPlace } from "@protocol/BoardPlace";
import type { BoardToolDef } from "@protocol/BoardToolDef";
import type { Kinds } from "@protocol/Kinds";
import type { Node } from "@protocol/Node";
import { nodeChain } from "./kinds";

/** A new sticky note's fill (and a note's when its style has none). */
export const NOTE_FILL = "#ffe680";

/** A new shape's fill (and a shape's when its style has none). */
export const SHAPE_FILL = "#cfe3ff";

/** The words' size when neither the item's style nor its element says (CSS pixels). */
export const WORDS_PX = 16;

/** What an element is when its kind says nothing more: the size, words and look of a new one. */
export interface ElementUsual {
  /** A new item's size, board units. */
  size: { w: number; h: number };
  /** A new item's words (empty: its kind's name). */
  words: string;
  /** A new item's look. */
  style: Record<string, string>;
}

/** CSS declarations by property name. */
export type Css = Record<string, string>;

/** An item's style values by key. */
export type Style = { readonly [key in string]?: string };

/** What the board hands a pack's element to draw its inside with. */
export interface ElementBodyProps {
  /** What its kind is on the board. */
  look: BoardLook;
  /** Its words and look. */
  text: string;
  style: Style;
  /** The project files it holds (a picture's). */
  attachments?: readonly string[];
  /** The item drawn, when it is one of the board's (not a part of another). */
  item?: {
    id: string;
    node: Node;
    /** Its place (board units). */
    place: BoardPlace;
    /** The part of it whose own words are being written ({@link ElementDef.partAt}; `null`: none). */
    editingPart: string | null;
    /** Ends writing a part's words. */
    done(): void;
  };
  /** The board's words written in place, for a part's own words. */
  Words: Component<{ text: string; editing: boolean; done: () => void; write: (text: string) => void }>;
  /** The board's drawing of another item's inside (an instance's parts), as its `look` says it is. */
  Inner: Component<{ look: BoardLook; text: string; style: Style; attachments?: readonly string[] }>;
}

/**
 * An element the board draws: the board's own ({@link BOARD_ELEMENTS}) or a GUI pack's. Its
 * name is what a kind's `board element=` writes; the rest says how it starts and looks.
 */
export interface ElementDef {
  /** Its name. */
  id: string;
  /** A new item's size, words and look, when its kind says none. */
  usual: ElementUsual;
  /** Its words are neither shown on it nor written on it (a picture, a drawing, an instance: its parts' are). */
  wordless?: boolean;
  /** Its words' size when its style says none (CSS pixels; {@link WORDS_PX} when not said). */
  wordsPx?: number;
  /** Where its words line up when its style says nothing (`center` when not said). */
  align?: "left" | "center" | "right";
  /** Its own declarations from its style, over the shared ones already in `css` (words, colour, corners); `align` is the style's alignment once checked. */
  look?(style: Style, css: Css, align: string | undefined): void;
  /** What it shows inside on the board, when not only its words (a pack's element). */
  Body?: Component<ElementBodyProps>;
  /** Its tool's picture: the shapes of a 16×16 line drawing (none: the board's own picture by the tool's `icon`, else a card's). */
  Icon?: Component;
  /** The part of it under `target` whose own words a double click writes (an instance's), if any. */
  partAt?(target: Element): string | null;
}

/** What a kind is on the board: as drawn, and as a new item starts. */
export interface BoardLook {
  /** The element it is drawn as. */
  element: string;
  /** That element. */
  def: ElementDef;
  /** A new item's size, board units. */
  size: { w: number; h: number };
  /** A new item's words (empty: its kind's name). */
  words: string;
  /** A new item's look. */
  style: Record<string, string>;
  /** Its name is not drawn on it (a UI rectangle's is only its layer's). */
  hidesName: boolean;
  /** The tool drawing it: its kind's own (a kind like another does not take its tool). */
  tool: BoardToolDef | null;
}

/** The elements, by name. */
export type Elements = ReadonlyMap<string, ElementDef>;

/** A card: what any kind nobody says anything about is. */
export const CARD: ElementDef = {
  id: "card",
  usual: { size: { w: 220, h: 120 }, words: "", style: {} },
  look(style, css) {
    css.background = safeColour(style.fill) ?? "var(--panel, #ffffff)";
    const stroke = safeColour(style.stroke);
    if (stroke) css["border-color"] = stroke;
  },
};

/** The board's own elements (Miro's: a square note, a wide text, a shape, a picture, a line drawn by hand, a card). */
export const BOARD_ELEMENTS: readonly ElementDef[] = [
  { id: "note", usual: { size: { w: 200, h: 200 }, words: "Note", style: { fill: NOTE_FILL } }, look: (style, css) => void (css.background = safeColour(style.fill) ?? NOTE_FILL) },
  { id: "text", usual: { size: { w: 280, h: 60 }, words: "Text", style: {} }, wordsPx: 18, align: "left", look: (style, css) => fillIf(style, css) },
  {
    id: "shape",
    usual: { size: { w: 220, h: 120 }, words: "Shape", style: { shape: "rectangle", fill: SHAPE_FILL } },
    look(style, css) {
      css.background = safeColour(style.fill) ?? SHAPE_FILL;
      const stroke = safeColour(style.stroke);
      if (stroke) css["border-color"] = stroke;
    },
  },
  { id: "image", usual: { size: { w: 320, h: 220 }, words: "Picture", style: {} }, wordless: true, look: (style, css) => void (css["object-fit"] = style.fit === "contain" ? "contain" : "cover") },
  { id: "drawing", usual: { size: { w: 220, h: 120 }, words: "Drawing", style: {} }, wordless: true },
  CARD,
];

/** The elements `defs` by name (a later one of the same name wins). */
export function elementsOf(defs: readonly ElementDef[]): Elements {
  return new Map(defs.map((d) => [d.id, d]));
}

/** The board's own elements by name: what draws when no pack adds any (and the tests'). */
export const OWN_ELEMENTS: Elements = elementsOf(BOARD_ELEMENTS);

/** The board's own picture element (a picture dropped comes in at its proportions, else this size). */
export const IMAGE_ELEMENT = OWN_ELEMENTS.get("image")!;

/** Sets the background from the style's fill, when it is a colour. */
export function fillIf(style: Style, css: Css): void {
  const fill = safeColour(style.fill);
  if (fill) css.background = fill;
}

/** What a kind nobody says anything about is on the board: a card. */
export const CARD_LOOK: BoardLook = { element: "card", def: CARD, ...CARD.usual, hidesName: false, tool: null };

/**
 * What node kind `kind` is on the board among `all`: each value its own `board` gives, else the
 * one of the kind it is like (and so on), else its element's usual one; its element is a card
 * when none along the way names one of `elements`. The tool is only the kind's own.
 */
export function boardLook(all: Kinds, kind: string, elements: Elements = OWN_ELEMENTS): BoardLook {
  const boards = nodeChain(all, kind).flatMap((id) => {
    const board = all.nodes.find((k) => k.id === id)?.board;
    return board ? [board] : [];
  });
  if (!boards.length) return CARD_LOOK;
  const def = boards.map((b) => (b.element ? elements.get(b.element) : undefined)).find((e) => !!e) ?? CARD;
  const usual = def.usual;
  const size = boards.map((b) => b.size).find((s) => !!s);
  const style = boards.map((b) => b.style).find((s) => !!s && Object.keys(s).length > 0);
  return {
    element: def.id,
    def,
    size: size ? { w: size[0], h: size[1] } : usual.size,
    words: boards.map((b) => b.words).find((w) => !!w) ?? usual.words,
    style: style ? { ...usual.style, ...style } : usual.style,
    hidesName: boards.some((b) => !!b.hides_name),
    tool: all.nodes.find((k) => k.id === kind)?.board?.tool ?? null,
  };
}

/** Whether an item shows its words: not one whose element is wordless (a picture, a drawing, an instance: its parts do), nor one whose kind hides its name. */
export function showsWords(look: Pick<BoardLook, "def" | "hidesName">): boolean {
  return !look.def.wordless && !look.hidesName;
}

/**
 * The fonts offered, by the word `style.font` holds: the families (those from Google Fonts are
 * loaded by `index.html`; without the network, the generic family after them).
 */
export const FONTS: Record<string, string> = {
  sans: "var(--font-ui, sans-serif)",
  rounded: "'Nunito', 'Arial Rounded MT Bold', sans-serif",
  display: "'Oswald', 'Arial Narrow', sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  elegant: "'Playfair Display', Georgia, serif",
  slab: "'Roboto Slab', Rockwell, serif",
  mono: "ui-monospace, 'Cascadia Code', monospace",
  typewriter: "'Courier Prime', 'Courier New', monospace",
  hand: "'Caveat', 'Comic Neue', 'Comic Sans MS', cursive",
  marker: "'Permanent Marker', 'Comic Sans MS', cursive",
};

/** The name each font of {@link FONTS} is offered under. */
export const FONT_NAMES: Record<string, string> = {
  sans: "Instrument Sans",
  rounded: "Nunito",
  display: "Oswald",
  serif: "Georgia",
  elegant: "Playfair Display",
  slab: "Roboto Slab",
  mono: "Monospace",
  typewriter: "Courier Prime",
  hand: "Caveat (handwriting)",
  marker: "Permanent Marker",
};

/** The smallest and biggest words an item may have (pixels). */
export const TEXT_SIZES = { min: 1, max: 400 } as const;

/** `value` when it is a colour (`#rgb`…`#rrggbbaa`, a colour's name, `rgb()`/`rgba()` of numbers), else `undefined`. */
export function safeColour(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const v = value.trim();
  return /^#[0-9a-f]{3,8}$/i.test(v) || /^[a-z]{3,20}$/i.test(v) || /^rgba?\(\s*[\d.]+%?\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?\s*(,\s*[\d.]+%?\s*)?\)$/i.test(v) ? v : undefined;
}

/** `value` as a number of pixels within `[min, max]`, else `fallback`. */
export function pixels(value: string | undefined, fallback: number, min = 0, max = 400): number {
  const n = Number(value);
  return value !== undefined && value !== "" && Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
}

/**
 * The look of an item drawn as `element` from its style, as CSS declarations: its words (size,
 * weight, slant, lines, alignment, font, colour) and corners, then what the element itself adds.
 */
export function lookOf(element: ElementDef, style: Style): Css {
  const css: Css = {};
  const set = (key: string, value: string | undefined) => value !== undefined && (css[key] = value);
  const align = style.align === "left" || style.align === "center" || style.align === "right" ? style.align : undefined;
  set("font-size", `${pixels(style.size, element.wordsPx ?? WORDS_PX, TEXT_SIZES.min, TEXT_SIZES.max)}px`);
  set("font-weight", style.weight === "bold" ? "700" : undefined);
  set("font-style", style.italic === "yes" ? "italic" : undefined);
  const lines = [style.underline === "yes" ? "underline" : "", style.strike === "yes" ? "line-through" : ""].filter(Boolean).join(" ");
  set("text-decoration", lines || undefined);
  set("text-align", align ?? element.align ?? "center");
  set("font-family", style.font ? FONTS[style.font] : undefined);
  set("color", safeColour(style.color));
  set("border-radius", style.radius !== undefined ? `${pixels(style.radius, 0)}px` : undefined);
  element.look?.(style, css, align);
  return css;
}

/** A frame's look (a screen, a component, a box inside a screen): its fill, outline and corners. */
export function frameLook(style: Style): Css {
  const css: Css = {};
  const fill = safeColour(style.fill);
  const stroke = safeColour(style.stroke);
  if (fill) css.background = fill;
  if (stroke) css["border-color"] = stroke;
  if (style.radius !== undefined) css["border-radius"] = `${pixels(style.radius, 0)}px`;
  return css;
}

/** Declarations as the text of a `style` attribute. */
export function cssText(css: Css): string {
  return Object.entries(css)
    .map(([k, v]) => `${k}:${v}`)
    .join(";");
}
